/**
 * PWA UI string catalog — English.
 *
 * 機翻草稿（draft）：以 zh-TW 來源語言對譯；上線前建議由 native 校對。
 * key 集合由 `Record<TranslationKey, string>` 強制與 zh-TW 對齊。
 */
import type { TranslationKey } from './zh-TW';

export const en: Record<TranslationKey, string> = {
  // ── common ───────────────────────────────────────────────
  'common.back': 'Back',
  'common.backHome': 'Back to home',
  'common.loading': 'Loading…',
  'common.unknownError': 'Unknown error',
  'common.logout': 'Log out',
  'common.logoutPending': 'Logging out…',

  // ── login ────────────────────────────────────────────────
  'login.brand': 'Yacht Ticketing',
  'login.title': 'Sign in',
  'login.emailPlaceholder': 'you@example.com',
  'login.email': 'Email',
  'login.password': 'Password',
  'login.submit': 'Sign in',
  'login.submitting': 'Signing in…',
  'login.err2fa': 'This account requires 2FA; the TOTP flow is not available in this scaffold yet.',
  'login.errBadCredentials': 'Incorrect email or password',
  'login.errGeneric': 'Sign-in failed, please try again later',

  // ── home ─────────────────────────────────────────────────
  'home.title': 'Yacht Products',
  'home.navMyOrders': 'My Orders',
  'home.navMyRefunds': 'My Refunds',
  'home.navRecommend': 'Trip Picks',
  'home.myTicketsHeading': 'My Tickets (latest {count})',
  'home.ticketsCapNote':
    'Showing the first 10; up to 50 kept (newest first; stored on this device)',
  'home.orderRef': 'Order {id}',
  'home.loadFailed': 'Failed to load products: ',
  'home.empty': 'No products available for booking.',
  'home.noMatch': 'No matching products; try adjusting your search or category.',
  'home.searchPlaceholder': 'Search product name or SKU…',
  'home.allCategories': 'All categories',
  'home.unpublished': ' (unpublished)',
  'home.buyNow': 'Buy now',
  'home.durationMin': ' · {minutes} min',

  // ── product detail ───────────────────────────────────────
  'product.title': 'Product Details',
  'product.loadFailed': 'Failed to load product: ',
  'product.durationLabel': ' · Duration {minutes} min',
  'product.skuLabel': 'SKU: ',
  'product.categoryLabel': 'Category: ',
  'product.chooseSchedule': 'Choose a schedule',
  'product.unpublished': 'Unpublished',

  // ── buy / schedule ───────────────────────────────────────
  'buy.title': 'Choose a Schedule',
  'buy.unitPrice': ' · Unit price {price}',
  'buy.loadingSchedules': 'Loading schedules…',
  'buy.scheduleLoadFailed': 'Failed to load schedules: ',
  'buy.noSchedules': 'No schedules available for this product.',
  'buy.until': 'Until {time}',
  'buy.cancelled': 'Cancelled',
  'buy.remaining': '{remaining} / {capacity} seats left',
  'buy.soldOut': 'Sold out',
  'buy.quantity': 'Quantity',
  'buy.subtotal': 'Subtotal: ',
  'buy.originalTotal': 'Original: ',
  'buy.promoSaved': 'You save: ',
  'buy.orderFailed': 'Order failed: ',
  'buy.submit': 'Place order',
  'buy.submitting': 'Creating order…',
  'buy.pkgLead': 'This package includes the following experiences — choose a time for each',
  'buy.pkgSegment': 'Part {n}',
  'buy.pkgPickEach': 'Choose a time for every part',
  'buy.pkgNoSlot': 'No available times for this part',
  'buy.pkgClash': 'Overlaps a chosen time',
  'buy.pkgNotSellable': 'This package is not available yet',
  'buy.pkgLimit': 'Up to {n} people for this package (limited by each part)',
  'buy.minutes': 'min',

  // ── checkout ─────────────────────────────────────────────
  'checkout.title': 'Payment',
  'checkout.orderNo': 'Order number: ',
  'checkout.demoNote':
    'Demo mode — this button calls the backend mock payment-callback directly, moving the order HOLD → PAYMENT_PENDING → PAID and issuing the tickets in the same transaction.',
  'checkout.reqFailed': 'Payment request failed: ',
  'checkout.retryHint': '. Please try again.',
  'checkout.pending': 'Processing…',
  'checkout.retry': 'Retry payment',
  'checkout.pay': 'Simulate successful payment',
  'checkout.oversoldTitle': 'Schedule Sold Out',
  'checkout.oversoldLine1':
    'Sorry — this schedule sold out the moment your payment was made, so the order could not be completed.',
  'checkout.oversoldLine2':
    'Your payment will be refunded automatically to the original method; we will notify you of the refund progress. No separate refund request is needed.',
  'checkout.failedTitle': 'Payment Unsuccessful',
  'checkout.failedLine1': 'This payment could not be completed and the order was not created.',
  'checkout.failedLine2':
    'Any amount charged will be refunded automatically; please return to the home page and order again.',
  'checkout.orderState': 'Order {id} · Status: {state}',
  'checkout.backReselect': 'Back to home to choose again',

  // ── order confirm ────────────────────────────────────────
  'confirm.title': 'Order Complete',
  'confirm.orderLabel': 'Order',
  'confirm.statusInline': 'Status: {state}',
  'confirm.statusLabel': 'Status',
  'confirm.paidAmountLabel': 'Amount paid',
  'confirm.paidAmount': 'Amount paid: ',
  'confirm.noTickets':
    'Ticket list not available; if you opened this page directly, please access individual tickets from your email or order list.',
  'confirm.issuedHeading': 'Issued tickets ({count})',
  'confirm.viewQr': 'View QR · {id}',

  // ── ticket QR ────────────────────────────────────────────
  'ticket.title': 'Ticket QR Code',
  'ticket.loadFailed': 'Failed to load ticket: ',
  'ticket.refundLocked': 'Refund locked',
  'ticket.refundEligible': 'Refundable',
  'ticket.qrNote':
    'Please show this QR code to staff for scanning on site; it carries a cryptographic signature and is tamper-proof.',
  'ticket.applyRefund': 'Request refund',

  // ── refund request ───────────────────────────────────────
  'refundReq.title': 'Request a Refund',
  'refundReq.backToTicket': 'Back to ticket',
  'refundReq.loadFailed': 'Failed to load ticket: ',
  'refundReq.ticketLabel': 'Ticket: ',
  'refundReq.statusInline': 'Status: {state}',
  'refundReq.cannotRefundState': 'This ticket is in state {state} and cannot be refunded.',
  'refundReq.lockedWindow':
    'The refund window has closed (REFUND_LOCKED); online refunds are not available.',
  'refundReq.reasonLabel': 'Refund reason (1–500 characters)',
  'refundReq.reasonPlaceholder': 'Please describe the reason for the refund, for our review.',
  'refundReq.charCount': '{count} / 500',
  'refundReq.submitFailed': 'Submission failed: ',
  'refundReq.submit': 'Submit refund request',
  'refundReq.submitting': 'Submitting…',
  'refundReq.footnote':
    'The refund amount follows the original order details; once submitted the refund process begins and boarding will be blocked immediately.',

  // ── refund detail ────────────────────────────────────────
  'refundDetail.title': 'Refund Progress',
  'refundDetail.loadFailed': 'Failed to load refund record: ',
  'refundDetail.refundNo': 'Refund number: ',
  'refundDetail.refundStatus': 'Refund status: ',
  'refundDetail.ticketStatus': 'Ticket status: ',
  'refundDetail.refundAmount': 'Refund amount: ',
  'refundDetail.allowancePending':
    'The amount has been refunded; the invoice allowance is still processing.',
  'refundDetail.manualReview':
    'This refund has entered manual review and will be handled by a specialist within 24 hours.',
  'refundDetail.pollingNote':
    'Progress updates automatically (10s); polling stops at a terminal state.',
  'refundDetail.viewTicket': 'View related ticket',

  // ── my orders ────────────────────────────────────────────
  'myOrders.title': 'My Orders',
  'myOrders.empty': 'No order records yet.',
  'myOrders.snapshotNote':
    'Status / amount are a snapshot from when the order was placed; up to 50 kept on this device.',

  // ── my refunds ───────────────────────────────────────────
  'myRefunds.title': 'My Refunds',
  'myRefunds.empty': 'No refund requests yet.',
  'myRefunds.ticketRef': 'Ticket {id}',
  'myRefunds.rowLoadFailed': 'Load failed',

  // ── order detail（D）──────────────────────────────────────
  'orderDetail.title': 'Order Details',
  'orderDetail.loadFailed': 'Failed to load order: ',
  'orderDetail.orderNumber': 'Order number: ',
  'orderDetail.statusLabel': 'Order status: ',
  'orderDetail.channelLabel': 'Channel: ',
  'orderDetail.subtotal': 'Subtotal: ',
  'orderDetail.total': 'Total: ',
  'orderDetail.createdAt': 'Created at: ',
  'orderDetail.holdExpiresAt': 'Held until: ',

  // ── account（F）──────────────────────────────────────────
  'account.title': 'Account',
  'account.loadFailed': 'Failed to load account information.',
  'account.displayName': 'Display name',
  'account.email': 'Email',
  'account.userId': 'User ID',
  'account.permissions': 'Permissions',
  'account.permissionsCount': '{count} item(s)',
  'account.twoFactor': 'Two-factor authentication',
  'account.twoFactorOn': 'Enabled',
  'account.twoFactorOff': 'Disabled',
  'account.readonlyNote':
    'This page is read-only; please contact support to change account details.',

  // ── 補充（D/E/F 共用）────────────────────────────────────
  'home.navAccount': 'Account',
  'ticket.canBoard': 'Boarding allowed',
  'ticket.cannotBoard': 'Boarding not allowed',
};
